import { useFormik } from "formik";
import { useEffect } from "react";
import Button from "src/components/buttons/Button";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { number, object } from "yup";
import { TAB_NAMES } from "../common/menu-navigation/menuNavigation";
import TabHeader from "../common/tab-header/TabHeader";
import { useCreateTax, useGetTax } from "./api-vendor";

const FORM_FIELDS = {
  TAX: "tax",
  GST: "gst",
};

const Settings = () => {
  useSetActiveTab(TAB_NAMES.SETTINGS);

  const { successNotification, errorNotification } = useNotification();

  const {
    isLoading: isGetTaxLoading,
    error,
    data,
    mutate,
    isValidating,
  } = useGetTax();

  const {
    isLoading: isSaveTaxLoading,
    isSuccess: createTaxSucceeded,
    execute,
    error: createTaxError,
  } = useCreateTax();

  useEffect(() => {
    if (createTaxSucceeded) {
      successNotification("Created successfully");
    } else if (createTaxError) {
      errorNotification("Something went wrong");
    }
  }, [
    createTaxError,
    createTaxSucceeded,
    errorNotification,
    successNotification,
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tax: data?.data?.[FORM_FIELDS.TAX] ?? 0,
      gst: data?.data?.[FORM_FIELDS.GST] ?? 0,
    },
    validationSchema: object({
      [FORM_FIELDS.GST]: number().required("Required"),
      [FORM_FIELDS.TAX]: number().required("Required"),
    }),
    onSubmit: (data) => execute({ data }),
  });

  return (
    <>
      <div className="flex flex-col gap-[20px]">
        <TabHeader label="Settings" />
        <div className="bg-white rounded-lg p-4 flex flex-col gap-8 min-h-[calc(100vh-239px)]">
          <div className="bg-[#F8F7F7] h-full flex flex-col gap-4 p-[30px] rounded-[10px] w-full md:w-1/2">
            <h1 className="font-semibold text-xl">Tax and GST</h1>
            <hr />
            <div className="flex gap-4">
              <div className="flex flex-col">
                <Input
                  type="number"
                  name={FORM_FIELDS.TAX}
                  label={"Tax"}
                  value={formik.values.tax.toString()}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue(FORM_FIELDS.TAX, parseInt(value ?? 0))
                  }
                  className="[&>div>div]:bg-white"
                  rightIcon={() => (
                    <span className="text-[#C0C0C0] border-l pl-2">%</span>
                  )}
                  required
                />
              </div>

              <div className="flex flex-col">
                <Input
                  type="number"
                  name={FORM_FIELDS.GST}
                  label={"GST"}
                  value={formik.values.gst.toString()}
                  onChange={({ target: { value } }) =>
                    formik.setFieldValue(FORM_FIELDS.GST, parseInt(value ?? 0))
                  }
                  className="[&>div>div]:bg-white"
                  rightIcon={() => (
                    <span className="text-[#C0C0C0] border-l pl-2">%</span>
                  )}
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex flex-row gap-3">
            <Button
              text={"Reset"}
              onClick={formik.resetForm}
              primary
              className="h-[40px] px-16 py-3"
              ghost
            />

            <Button
              text={isGetTaxLoading || isSaveTaxLoading ? "Loading..." : "Save"}
              onClick={formik.handleSubmit}
              primary
              className="h-[40px] px-16 py-3"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
